import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, Typography, Chip, Avatar, CircularProgress, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import SearchBar from '../../components/SearchBar';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { toast } from 'react-toastify';
import { getLiveStreams } from '../../api/endpoints/chat';

const LiveStreams = () => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchStreams = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await getLiveStreams(params);
      setStreams(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load live streams');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => { fetchStreams(); }, [fetchStreams]);

  const filtered = search
    ? streams.filter((s) => s.title?.toLowerCase().includes(search.toLowerCase()) || s.astrologerId?.name?.toLowerCase().includes(search.toLowerCase()))
    : streams;

  const columns = [
    { id: 'title', label: 'Stream', render: (row) => (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar src={row.thumbnail} variant="rounded" sx={{ width: 56, height: 32 }} />
        <Box>
          <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
          <Typography variant="caption" color="text.secondary">{row.astrologerId?.name || '-'}</Typography>
        </Box>
      </Stack>
    )},
    { id: 'viewerCount', label: 'Viewers', align: 'center' },
    { id: 'totalDonations', label: 'Donations', align: 'right', render: (row) => formatCurrency(row.totalDonations || 0) },
    { id: 'status', label: 'Status', render: (row) => {
      const colors = { live: 'error', scheduled: 'info', ended: 'default', cancelled: 'default' };
      return <Chip label={row.status} size="small" color={colors[row.status] || 'default'} />;
    }},
    { id: 'createdAt', label: 'Scheduled', render: (row) => formatDateTime(row.scheduledAt || row.createdAt) },
  ];

  return (
    <Box>
      <PageHeader title="Live Streams" subtitle="Manage and monitor live streaming sessions" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search streams..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="live">Live</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="ended">Ended</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable columns={columns} rows={filtered} page={page} rowsPerPage={rowsPerPage} total={total}
          onPageChange={setPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} loading={loading} />
      )}
    </Box>
  );
};

export default LiveStreams;
